'use client';
import { ReactNode, useCallback, useLayoutEffect, useRef } from 'react';
import cn from 'classnames';
import { Swiper, SwiperSlide, SwiperRef } from 'swiper/react';
import type { Swiper as SwiperInstance } from 'swiper';
import { Autoplay, FreeMode, Navigation, Pagination, Virtual, EffectFade } from 'swiper/modules';
import styles from './carousel2.module.sass';
import React from 'react';
import { FontIcon } from '@intigral/shared';
import { Top1, Top2, Top3, Top4, Top5, Top6, Top7, Top8, Top9, Top10 } from '@intigral/shared';
import 'swiper/css/effect-fade'; // fade effect styles

type Slider = {
    swiperSettings?: Record<string, any>;
    children?: ReactNode | ReactNode[];
    dir?: string;
    isLoading?: boolean;
    top10?: boolean;
    reachEnd?: (swiper: SwiperInstance) => void;
    immediatePauseOnHover?: boolean;
};

const TopIcons = [Top1, Top2, Top3, Top4, Top5, Top6, Top7, Top8, Top9, Top10];

// keep in sync with $medium; Swiper tiers are sticky
const ARROWS_FROM = 924;

const withDragRule = (settings: Record<string, any>, hasArrows: boolean) => {
    if (!hasArrows) return settings;
    const tiers: Record<string, any> = { ...settings.breakpoints };
    // an empty tier would reset slidesPerView
    if (!(ARROWS_FROM in tiers)) {
        tiers[ARROWS_FROM] = Object.keys(tiers)
            .filter((key) => !key.startsWith('@') && parseFloat(key) < ARROWS_FROM)
            .sort((a, b) => parseFloat(a) - parseFloat(b))
            .reduce((acc, key) => ({ ...acc, ...tiers[key] }), {});
    }
    Object.keys(tiers).forEach((key) => {
        if (key.startsWith('@')) return;
        const draggable = parseFloat(key) < ARROWS_FROM;
        // simulateTouch too, or jittery clicks miss the card
        tiers[key] = { ...tiers[key], allowTouchMove: draggable, simulateTouch: draggable };
    });
    return { ...settings, allowTouchMove: true, simulateTouch: true, breakpoints: tiers };
};

export const Carousel2 = React.forwardRef(
    (
        { swiperSettings, children, dir, isLoading, top10, reachEnd, immediatePauseOnHover, ...props }: Slider,
        ref: React.Ref<SwiperRef>
    ) => {
        const holderRef = useRef<HTMLDivElement>(null);
        const hasArrows = swiperSettings?.navigation !== false;

        const syncPosterHeight = useCallback(() => {
            const holder = holderRef.current;
            if (!holder) return;
            const slide = holder.querySelector<HTMLElement>('[class*="swiper-slide"]');
            const poster = slide?.querySelector<HTMLElement>('[data-poster]');
            if (!slide || !poster) {
                holder.style.removeProperty('--carousel-poster-height');
                return;
            }
            const height = poster.getBoundingClientRect().bottom - slide.getBoundingClientRect().top;
            // ceil, not round, or the poster peeks under the arrow
            if (height > 0) holder.style.setProperty('--carousel-poster-height', `${Math.ceil(height)}px`);
        }, []);

        useLayoutEffect(() => {
            syncPosterHeight();

            const holder = holderRef.current;
            if (!holder || typeof ResizeObserver === 'undefined') return;
            const observer = new ResizeObserver(syncPosterHeight);
            observer.observe(holder);
            const poster = holder.querySelector<HTMLElement>('[class*="swiper-slide"] [data-poster]');
            if (poster) observer.observe(poster);
            return () => observer.disconnect();
        }, [syncPosterHeight, children]);

        const defaultSettings = {
            modules: [Autoplay, FreeMode, Navigation, Pagination, Virtual, EffectFade],
            navigation: {
                nextEl: '.swb-next',
                prevEl: '.swb-prev',
            },
            freeMode: true,
            loop: false,
            speed: 500,
            slideActiveClass: 'reset-swiper-class',
            slideNextClass: 'reset-swiper-class',
            loopAddBlankSlides: false,
            touchStartPreventDefault: false,
            slidesPerGroup: 2,
            slidesPerView: 3,
            breakpoints: {
                560: {
                    slidesPerView: 3,
                },
                768: {
                    slidesPerView: 4,
                    slidesPerGroup: 4,
                },
                924: {
                    slidesPerView: 4,
                    slidesPerGroup: 4,
                },
                1212: {
                    slidesPerView: 6,
                    slidesPerGroup: 4,
                },
                1500: {
                    slidesPerView: 7,
                    slidesPerGroup: 4,
                },
                1620: {
                    slidesPerView: 7,
                    slidesPerGroup: 4,
                },
            },
            // virtual: {
            //     addSlidesBefore: 6,
            //     addSlidesAfter: 6,
            // },
            ...swiperSettings,
            ...(immediatePauseOnHover && {
                onInit: (swiper: any) => {
                    const el = swiper.el as HTMLElement;
                    const baseSpeed = swiper.params?.speed ?? 400;

                    let pausedTranslate: number | null = null;
                    const originalDelay = swiper.params?.autoplay?.delay ?? 0;

                    const stopNow = () => {
                        if (swiper.autoplay && typeof swiper.autoplay.pause === 'function') {
                            swiper.autoplay.pause();
                        } else {
                            swiper.autoplay?.stop();
                        }

                        const cur = swiper.getTranslate ? swiper.getTranslate() : 0;
                        pausedTranslate = cur;

                        if (swiper.setTransition) swiper.setTransition(0);
                        if (swiper.setTranslate) swiper.setTranslate(cur);

                        if (swiper.wrapperEl) swiper.wrapperEl.style.transitionDuration = '0ms';

                        swiper.allowSlideNext = false;
                        swiper.allowSlidePrev = false;

                        (swiper as any).animating = false;
                    };

                    const startNow = () => {
                        swiper.allowSlideNext = true;
                        swiper.allowSlidePrev = true;

                        const rawMin = typeof swiper.minTranslate === 'function' ? swiper.minTranslate() : -Infinity;
                        const rawMax = typeof swiper.maxTranslate === 'function' ? swiper.maxTranslate() : Infinity;
                        const beforeFix = swiper.getTranslate ? swiper.getTranslate() : 0;
                        const low = Math.min(rawMin, rawMax);
                        const high = Math.max(rawMin, rawMax);
                        const needsFix = beforeFix < low - 2 || beforeFix > high + 2; // tolerance

                        if (needsFix && typeof swiper.loopFix === 'function') {
                            swiper.loopFix();
                        }

                        if (swiper.setTransition) swiper.setTransition(0);
                        if (pausedTranslate != null && swiper.setTranslate) {
                            swiper.setTranslate(pausedTranslate);
                        }

                        try {
                            if (typeof (swiper as any).updateActiveIndex === 'function') {
                                try {
                                    (swiper as any).updateActiveIndex();
                                } catch {
                                    (swiper as any).updateActiveIndex(pausedTranslate);
                                }
                            }
                            if (typeof (swiper as any).updateSlidesClasses === 'function') {
                                (swiper as any).updateSlidesClasses();
                            }
                        } catch {
                            // console.log('Pause in swiper error');
                        }

                        // forced reflow is load-bearing
                        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                        swiper.wrapperEl && (swiper.wrapperEl as HTMLElement).offsetHeight;

                        if (swiper.wrapperEl) swiper.wrapperEl.style.transitionDuration = '';
                        if (swiper.setTransition) swiper.setTransition(baseSpeed);

                        if (swiper.autoplay && typeof swiper.autoplay.resume === 'function') {
                            swiper.params.autoplay.delay = originalDelay || 0; // keep configured delay (0 allowed)
                            swiper.autoplay.resume();
                        } else {
                            if (swiper.params.autoplay)
                                swiper.params.autoplay.delay = Math.max(100, originalDelay || 0);
                            swiper.params.speed = baseSpeed;
                            swiper.autoplay?.start();
                            setTimeout(() => {
                                if (swiper?.params?.autoplay) swiper.params.autoplay.delay = originalDelay || 0;
                            }, 0);
                        }

                        pausedTranslate = null;
                    };

                    el.addEventListener('mouseenter', stopNow);
                    el.addEventListener('mouseleave', startNow);

                    swiper.once('destroy', () => {
                        el.removeEventListener('mouseenter', stopNow);
                        el.removeEventListener('mouseleave', startNow);
                    });
                },
            }),
        };
        const top10Settings = {
            slidesPerView: 2,
            slidesPerGroup: 1,
            breakpoints: {
                560: {
                    slidesPerView: 2,
                    slidesPerGroup: 1,
                },
                768: {
                    slidesPerView: 3,
                    slidesPerGroup: 1,
                },
                924: {
                    slidesPerView: 3,
                    slidesPerGroup: 1,
                },
                1212: {
                    slidesPerView: 4,
                    slidesPerGroup: 1,
                },
                1500: {
                    slidesPerView: 5,
                    slidesPerGroup: 1,
                },
                1620: {
                    slidesPerView: 5,
                    slidesPerGroup: 1,
                },
            },
        };
        if (isLoading) {
            return (
                <div className={styles.holder}>
                    <Swiper
                        dir={dir}
                        className={cn(styles.swiper)}
                        wrapperClass={cn(styles['swiper-cont'])}
                        {...withDragRule(
                            { ...defaultSettings, ...(top10 ? top10Settings : {}), ...swiperSettings, ...props },
                            hasArrows
                        )}
                        ref={ref}
                    >
                        {Array(7)
                            .fill('')
                            .map((_, index) => (
                                <SwiperSlide
                                    className={styles['swiper-slide']}
                                    key={`loading-slide-${index}`}
                                    virtualIndex={index}
                                >
                                    <div className={cn(top10 && styles.top10)}>{children && children}</div>
                                </SwiperSlide>
                            ))}

                        {hasArrows && (
                            <>
                                <div className={cn(styles.prev, 'swb-prev')}>
                                    <FontIcon name="arrow-left" md="small"></FontIcon>
                                </div>
                                <div className={cn(styles.next, 'swb-next')}>
                                    <FontIcon name="arrow-right" md="small"></FontIcon>
                                </div>
                            </>
                        )}
                    </Swiper>
                </div>
            );
        }

        return (
            <div className={styles.holder} ref={holderRef}>
                <Swiper
                    dir={dir}
                    key={dir}
                    className={cn(styles.swiper)}
                    wrapperClass={cn(styles['swiper-cont'])}
                    {...withDragRule(
                        { ...defaultSettings, ...(top10 ? top10Settings : {}), ...swiperSettings, ...props },
                        hasArrows
                    )}
                    ref={ref}
                    onReachEnd={reachEnd}
                >
                    {Array.isArray(children)
                        ? children.flat().map(
                              (item, index) =>
                                  item && (
                                      <SwiperSlide
                                          className={styles['swiper-slide']}
                                          key={`slide-${index}`}
                                          //   virtualIndex={Date.now() + Math.floor(Math.random() * 100) + index * 100}
                                      >
                                          {top10 ? (
                                              <div className={styles.top10}>
                                                  {TopIcons[index] ? (
                                                      <div className={styles.number}>
                                                          {React.createElement(TopIcons[index])}
                                                      </div>
                                                  ) : null}
                                                  {item}
                                              </div>
                                          ) : (
                                              item
                                          )}
                                      </SwiperSlide>
                                  )
                          )
                        : children && <SwiperSlide className={styles['swiper-slide']}>{children}</SwiperSlide>}

                    {hasArrows && (
                        <>
                            <div className={cn(styles.prev, 'swb-prev')}>
                                <FontIcon name="arrow-left" md="small"></FontIcon>
                            </div>
                            <div className={cn(styles.next, 'swb-next')}>
                                <FontIcon name="arrow-right" md="small"></FontIcon>
                            </div>
                        </>
                    )}
                </Swiper>
            </div>
        );
    }
);
